import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Logo, TAGLINE } from '@norvix/ui';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { homePath } from '../auth/capabilities.js';
import { MfaEnrollmentForm } from '../auth/MfaEnrollmentForm.js';
import { useAuth } from '../auth/useAuth.js';

export function LoginPage(): JSX.Element {
  const { login, user } = useAuth();
  const [enrolled, setEnrolled] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showTotp, setShowTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password, totpCode || undefined);
      setPassword('');
      setTotpCode('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      // If the API tells us the account requires TOTP, reveal the field and prompt.
      if (/totp/i.test(message)) {
        setShowTotp(true);
        setError('This account requires a TOTP code — enter it below.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (user && !user.mfaEnrollmentRequired) {
    return <Navigate to={homePath(user.role)} replace />;
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ p: 3 }}>
      <Card sx={{ width: 460, p: 1, boxShadow: 'var(--shadow-card-deep)' }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={1} alignItems="flex-start" sx={{ mb: 3 }}>
            <Logo height={28} glow />
            <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              Back Office
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{TAGLINE}</Typography>
          </Stack>

          {user?.mfaEnrollmentRequired ? <MfaEnrollmentForm onComplete={() => setEnrolled(true)} /> : <>
          <Typography variant="h4" gutterBottom>Sign in</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Sign in with your email, password and authenticator code. New staff accounts will be guided through authenticator setup.
          </Typography>

          {enrolled && <Alert severity="success" sx={{ mb: 2 }}>Authenticator enabled. Sign in again with a fresh code.</Alert>}
          {error !== null && <Alert severity={showTotp ? 'info' : 'error'} sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={(e) => void onSubmit(e)}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                autoFocus
                fullWidth
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                fullWidth
              />
              {showTotp ? (
                <TextField
                  label="TOTP code"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  inputProps={{ pattern: '[0-9]{6}', maxLength: 6, inputMode: 'numeric' }}
                  required
                  fullWidth
                  helperText="6 digits from your authenticator app"
                />
              ) : (
                <Box sx={{ textAlign: 'right' }}>
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={() => setShowTotp(true)}
                    sx={{ fontSize: 13, color: 'text.secondary' }}
                  >
                    I have a TOTP code
                  </MuiLink>
                </Box>
              )}
              <Button type="submit" variant="contained" size="large" disabled={loading} fullWidth>
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </Stack>
          </form>
          </>}
        </CardContent>
      </Card>
    </Box>
  );
}
